import {
  type GridRowData,
  type GridColumns,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { CustomDataGrid, DialogBox } from 'components';
import { colorTheme } from 'theme';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { strings } from 'i18n/Strings';
import {
  exportOrdersInDetails,
  getAllUsersOrderByDetail,
  getAllUsersOrderTotalByDetail,
} from 'service/OrderService';
import {
  Typography,
  useTheme,
  TextField,
  Select,
  MenuItem,
  Grid,
  InputLabel,
  FormControl,
  Tooltip,
} from '@mui/material';
import { CurrencyFormater } from 'components/helpers/commonFunctions/CurrencyFormater';
import {
  type OrderDetailsAttributesType,
  type OrderTotalsDetailsAttributesType,
} from 'types/services';
import { LoadingButton } from '@mui/lab';
import OrderStatusSummary from './OrderStatusSummary';
import {
  exportExcelFileNames,
  statusOptions,
  transactionTypeOptions,
} from 'constants/constantText';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import { type RootState } from 'store';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';
import {
  checkSortingValues,
  dateFormat,
  formatToSevenFixedDecimals,
  getDescriptionAndShortDescription,
} from 'utils/GeneralUtil';

export interface OrderFilterType {
  startDate?: string | undefined;
  endDate?: string;
  status?: string;
  orderType?: string;
  employerId?: string;
}

const OrdersSummary = (): JSX.Element => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const colors = colorTheme(theme.palette.mode);
  const [ordersSummaryData, setOrdersSummaryData] = useState<
    OrderDetailsAttributesType[]
  >([]);
  const [ordersTotalSummaryData, setOrdersTotalSummaryData] = useState<
    OrderTotalsDetailsAttributesType[]
  >([]);
  const pageInfo = useSelector((state) => state?.pagination?.ordersSummary);
  const { allMasterFunds } = useSelector((state: RootState) => state.fund);
  const [pageCount, setPageCount] = useState(null);
  const [filterLoader, setFilterLoader] = useState<boolean>(false);
  const [exportLoader, setExportLoader] = useState<boolean>(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [orderDialogData, setOrderDialogData] = useState(false);
  const [dateError, setDateError] = useState<string>('');

  const [filters, setFilters] = useState<OrderFilterType>({
    startDate: undefined,
    endDate: undefined,
    status: 'All',
    orderType: 'All',
    employerId: '',
  });

  const handleChatDialogClose = (): void => {
    setIsModalOpen(false);
  };

  const handleFilterChange = (filterType: string, value: string): void => {
    const updatedFilters = {
      ...filters,
      [filterType]: value,
    };

    // Validate dates
    if (filterType === 'startDate' || filterType === 'endDate') {
      if (updatedFilters.startDate && updatedFilters.endDate) {
        const startDate = new Date(updatedFilters.startDate);
        const endDate = new Date(updatedFilters.endDate);
        
        if (endDate < startDate) {
          setDateError('End date cannot be before start date');
        } else {
          setDateError('');
        }
      } else {
        setDateError('');
      }
    }

    setFilters(updatedFilters);
  };

  const getAllOrdersSummary = async (): Promise<void> => {
    try {
      const data = await getAllUsersOrderByDetail(
        Number(pageInfo?.page) + 1,
        pageInfo?.pageSize,
        pageInfo?.sortBy,
        pageInfo?.sortOrder,
        filters,
      );
      setPageCount(data?.count);
      setOrdersSummaryData(data?.rows);
    } catch (error) {
      console.log(error);
    }
    setFilterLoader(false);
  };

  const getAllOrdersTotalSummary = async (): Promise<void> => {
    try {
      const data = await getAllUsersOrderTotalByDetail(filters);
      setOrdersTotalSummaryData([data]);
    } catch (error) {
      console.log(error);
    }
  };

  const downloadExcelFile = async (): Promise<void> => {
    setExportLoader(true);
    
    if (dateError) {
      setExportLoader(false);
      dispatch(
        setSnackBarFailed({
          snackBarMessage: dateError,
        }),
      );
      return;
    }
    
    if (filters.startDate && filters.endDate) {
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      
      if (endDate < startDate) {
        setExportLoader(false);
        dispatch(
          setSnackBarFailed({
            snackBarMessage: 'End date cannot be before start date',
          }),
        );
        return;
      }
      
      const dateDifference =
        (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24);

      if (dateDifference > 60) {
        setExportLoader(false);
        dispatch(
          setSnackBarFailed({
            snackBarMessage: strings.displayText.dateExceeded,
          }),
        );
        return;
      }
    }
    if (!filters.startDate || !filters.endDate) {
      setExportLoader(false);
      dispatch(
        setSnackBarFailed({
          snackBarMessage: strings.displayText.selectDateRange,
        }),
      );
      return;
    }
    try {
      const url = await exportOrdersInDetails(filters);
      const link = document.createElement('a');
      link.href = url as string;
      link.download = exportExcelFileNames.ORDER_SUMMARY;
      link.click();
      dispatch(
        setSnackBarSuccess({
          snackBarMessage: strings.displayText.orderSummaryExportSuccess,
        }),
      );
    } catch (error) {
      dispatch(
        setSnackBarSuccess({
          snackBarMessage: strings.displayText.orderSummaryExportFail,
        }),
      );
    } finally {
      setExportLoader(false);
    }
  };

  useEffect(() => {
    void getAllOrdersSummary();
  }, [
    pageInfo?.page,
    pageInfo.pageSize,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
  ]);

  useEffect(() => {
    void getAllOrdersTotalSummary();
  }, []);

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'createdAt',
      headerName: 'date',
      width: 120,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.dateColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row?.createdAt) || '-'}
        </Typography>
      ),
    },
    {
      field: 'userFunds.userInfo.firstName',
      headerName: 'employeeName',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.employeeName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {`${String(
            row?.userFunds[0]?.userInfo?.firstName ||
              row?.userSalesUnits[0]?.userInfo?.firstName,
          )} ${String(
            row?.userFunds[0]?.userInfo?.lastName ||
              row?.userSalesUnits[0]?.userInfo?.lastName,
          )}` || '-'}
        </Typography>
      ),
    },
    {
      field: 'userFunds.userInfo.uniqueId',
      headerName: 'employeeID',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.employeeID}
        </Typography>
      ),
      renderCell: ({ row }) => {
        const orderInfo =
          row?.userFunds[0]?.orderInfo || row?.userSalesUnits[0]?.orderInfo;
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            <a
              style={
                orderInfo
                  ? {
                      color: 'blue',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }
                  : { textDecoration: 'none' }
              }
              onClick={() => {
                if (!orderInfo) {
                  dispatch(
                    setSnackBarFailed({
                      snackBarMessage: strings.displayText.orderPendingEmployee,
                    }),
                  );
                  return;
                }
                setOrderDialogData(row);
                setIsModalOpen(true);
              }}
            >
              {row?.userFunds[0]?.userInfo?.uniqueId ||
                row?.userSalesUnits[0]?.userInfo?.uniqueId ||
                '-'}
            </a>
          </Typography>
        );
      },
    },
    {
      field: 'status',
      headerName: 'status',
      width: 120,
      sortable: false,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.orderStatus}
        </Typography>
      ),
      renderCell: ({ row }) => {
        const pendingSubmitUserFundsStatus = row?.userFunds.find(
          (val) =>
            val?.orderInfo?.orderStatus === strings.displayText.pendingLabel,
        );
        const pendingSubmitUserSalesUnitsStatus = row?.userSalesUnits.find(
          (val) =>
            val?.orderInfo?.orderStatus === strings.displayText.pendingLabel,
        );
        const submittedUserFundsSStatus = row?.userFunds.find(
          (val) =>
            val?.orderInfo?.orderStatus === strings.displayText.submittedLabel,
        );
        const submittedUserSalesUnitsStatus = row?.userSalesUnits.find(
          (val) =>
            val?.orderInfo?.orderStatus === strings.displayText.submittedLabel,
        );
        const filledUserFundsStatus = row?.userFunds.find(
          (val) =>
            val?.orderInfo?.orderStatus === strings.displayText.filledLabel,
        );
        const filledUserSalesUnitsStatus = row?.userSalesUnits.find(
          (val) =>
            val?.orderInfo?.orderStatus === strings.displayText.filledLabel,
        );
        const cancelledUserFundsStatus = row?.userFunds.find(
          (val) =>
            val?.orderInfo?.orderStatus === strings.displayText.cancelledLabel,
        );
        const cancelledUserSalesUnitsStatus = row?.userSalesUnits.find(
          (val) =>
            val?.orderInfo?.orderStatus === strings.displayText.cancelledLabel,
        );
        let status;

        if (pendingSubmitUserFundsStatus || pendingSubmitUserSalesUnitsStatus) {
          status = strings.displayText.pendingLabel;
        } else if (submittedUserFundsSStatus || submittedUserSalesUnitsStatus) {
          status = strings.displayText.submittedLabel;
        } else if (filledUserFundsStatus || filledUserSalesUnitsStatus) {
          status = strings.displayText.filledLabel;
        } else if (cancelledUserFundsStatus || cancelledUserSalesUnitsStatus) {
          status = strings.displayText.cancelledLabel;
        } else {
          status = strings.displayText.notPlacedLabel;
        }

        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {status}
          </Typography>
        );
      },
    },
    {
      field: 'transactionType',
      headerName: 'orderType',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.orderType}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.transactionType || '-'}
        </Typography>
      ),
    },
    {
      field: 'userSalesUnits.userInfo.organizationInfo.uniqueId',
      headerName: 'employerId',
      width: 120,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.employerId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.userFunds[0]?.userInfo?.organizationInfo?.uniqueId ||
            row?.userSalesUnits[0]?.userInfo?.organizationInfo?.uniqueId ||
            '-'}
        </Typography>
      ),
    },
    {
      field: 'userSalesUnits.pensionPlanInfo.name',
      headerName: 'plan',
      width: 100,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.pensionPlanLabel}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.userFunds[0]?.pensionPlanInfo?.name ||
            row?.userSalesUnits[0]?.pensionPlanInfo?.name ||
            '-'}
        </Typography>
      ),
    },
    {
      field: 'userSalesUnits.planInfo.planName',
      headerName: 'retirementPlan',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.retirementPlanLabel}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.userFunds[0]?.planInfo?.planName ||
            row?.userSalesUnits[0]?.planInfo?.planName ||
            '-'}
        </Typography>
      ),
    },
    {
      field: 'contributionFileTotal',
      headerName: 'contributionFileTotal',
      sortable: false,
      align: 'right',
      width: 180,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.contributionFileTotalER}
        </Typography>
      ),
      renderCell: ({ row }) => {
        const employerContribtuion =
          row?.contributionFileInfo?.contributionRecords
            ?.map((val: { employerContribution: string | number }) =>
              Number(val.employerContribution),
            )
            ?.reduce((acc: number, amount: number) => acc + amount, 0);

        const employeeContribution =
          row?.contributionFileInfo?.contributionRecords
            ?.map((val: { employeeContribution: string | number }) =>
              Number(val.employeeContribution),
            )
            .reduce((acc: number, amount: number) => acc + amount, 0);

        const totalContributionInFile =
          Number(employeeContribution) + Number(employerContribtuion);

        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {CurrencyFormater(Number(totalContributionInFile))}
          </Typography>
        );
      },
    },
    {
      field: 'amount',
      headerName: 'contribution',
      align: 'right',
      width: 250,
      renderHeader: () => (
        <Tooltip
          title="Client Contrib’n (Buy)/Sales Proceeds (Sell)"
          placement="top"
        >
          <Typography
            variant="h5"
            color={colors.gray[100]}
            fontSize={14}
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
              display: 'block',
            }}
          >
            {strings.displayText.contributionSalesProceeds}
          </Typography>
        </Tooltip>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {CurrencyFormater(Number(row?.amount))}
        </Typography>
      ),
    },
    {
      field: 'fund1',
      headerName: 'fund1',
      sortable: false,
      headerAlign: 'right',
      align: 'right',
      width: 250,
      renderHeader: () => {
        const descObj = getDescriptionAndShortDescription(
          allMasterFunds,
          strings.displayText.fundValue1,
        );
        return (
          <Tooltip title={descObj.description} placement="top">
            <Typography
              variant="h5"
              color={colors.gray[100]}
              fontSize={14}
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                display: 'block',
              }}
            >
              {descObj.shortDescription}
            </Typography>
          </Tooltip>
        );
      },
      renderCell: ({ row }) => {
        const buyFund =
          row?.userFunds.length > 0 &&
          row?.userFunds?.find(
            (val) => val?.fundInfo?.name === strings.displayText.fundValue1,
          );
        const sellFund =
          row?.userSalesUnits.length > 0 &&
          row?.userSalesUnits?.find(
            (val) => val?.fundInfo?.name === strings.displayText.fundValue1,
          );
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {buyFund
              ? CurrencyFormater(Number(buyFund?.splitAmount))
              : CurrencyFormater(Number(sellFund?.unitAmount)) || '-'}
          </Typography>
        );
      },
    },
    {
      field: 'fund2',
      headerName: 'fund2',
      sortable: false,
      width: 250,
      headerAlign: 'right',
      align: 'right',
      renderHeader: () => {
        const descObj = getDescriptionAndShortDescription(
          allMasterFunds,
          strings.displayText.fundValue2,
        );

        return (
          <Tooltip title={descObj.description}>
            <Typography
              variant="h5"
              color={colors.gray[100]}
              fontSize={14}
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                display: 'block',
              }}
            >
              {descObj.shortDescription}
            </Typography>
          </Tooltip>
        );
      },
      renderCell: ({ row }) => {
        const buyFund =
          row?.userFunds.length > 0 &&
          row?.userFunds?.find(
            (val) => val?.fundInfo?.name === strings.displayText.fundValue2,
          );
        const sellFund =
          row?.userSalesUnits.length > 0 &&
          row?.userSalesUnits?.find(
            (val) => val?.fundInfo?.name === strings.displayText.fundValue2,
          );
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {buyFund
              ? CurrencyFormater(Number(buyFund?.splitAmount))
              : CurrencyFormater(Number(sellFund?.unitAmount)) || '-'}
          </Typography>
        );
      },
    },
    {
      field: 'fund3',
      headerName: 'fund3',
      sortable: false,
      width: 250,
      headerAlign: 'right',
      align: 'right',
      renderHeader: () => {
        const descObj = getDescriptionAndShortDescription(
          allMasterFunds,
          strings.displayText.fundValue3,
        );
        return (
          <Tooltip title={descObj.description}>
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {descObj.shortDescription}
            </Typography>
          </Tooltip>
        );
      },
      renderCell: ({ row }) => {
        const buyFund =
          row?.userFunds.length > 0 &&
          row?.userFunds?.find(
            (val) => val?.fundInfo?.name === strings.displayText.fundValue3,
          );
        const sellFund =
          row?.userSalesUnits.length > 0 &&
          row?.userSalesUnits?.find(
            (val) => val?.fundInfo?.name === strings.displayText.fundValue3,
          );
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {buyFund
              ? CurrencyFormater(Number(buyFund?.splitAmount))
              : CurrencyFormater(Number(sellFund?.unitAmount)) || '-'}
          </Typography>
        );
      },
    },
    {
      field: 'fund4',
      headerName: 'fund4',
      sortable: false,
      width: 250,
      headerAlign: 'right',
      align: 'right',
      renderHeader: () => {
        const descObj = getDescriptionAndShortDescription(
          allMasterFunds,
          strings.displayText.fundValue4,
        );
        return (
          <Tooltip title={descObj.description} placement="top">
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {descObj.shortDescription}
            </Typography>
          </Tooltip>
        );
      },
      renderCell: ({ row }) => {
        const buyFund =
          row?.userFunds.length > 0 &&
          row?.userFunds?.find(
            (val) => val?.fundInfo?.name === strings.displayText.fundValue4,
          );
        const sellFund =
          row?.userSalesUnits.length > 0 &&
          row?.userSalesUnits?.find(
            (val) => val?.fundInfo?.name === strings.displayText.fundValue4,
          );
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {buyFund
              ? CurrencyFormater(Number(buyFund?.splitAmount))
              : CurrencyFormater(Number(sellFund?.unitAmount)) || '-'}
          </Typography>
        );
      },
    },
    {
      field: 'fund5',
      headerName: 'fund5',
      sortable: false,
      width: 250,
      headerAlign: 'right',
      align: 'right',
      renderHeader: () => {
        const descObj = getDescriptionAndShortDescription(
          allMasterFunds,
          strings.displayText.fundValue5,
        );
        return (
          <Tooltip title={descObj.description} placement="top">
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {descObj.shortDescription}
            </Typography>
          </Tooltip>
        );
      },
      renderCell: ({ row }) => {
        const buyFund =
          row?.userFunds.length > 0 &&
          row?.userFunds?.find(
            (val) => val?.fundInfo?.name === strings.displayText.fundValue5,
          );
        const sellFund =
          row?.userSalesUnits.length > 0 &&
          row?.userSalesUnits?.find(
            (val) => val?.fundInfo?.name === strings.displayText.fundValue5,
          );
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {buyFund
              ? CurrencyFormater(Number(buyFund?.splitAmount))
              : CurrencyFormater(Number(sellFund?.unitAmount)) || '-'}
          </Typography>
        );
      },
    },
    {
      field: 'fund6',
      headerName: 'fund6',
      sortable: false,
      width: 250,
      headerAlign: 'right',
      align: 'right',
      renderHeader: () => {
        const descObj = getDescriptionAndShortDescription(
          allMasterFunds,
          strings.displayText.fundValue6,
        );
        return (
          <Tooltip title={descObj.description}>
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {descObj.shortDescription}
            </Typography>
          </Tooltip>
        );
      },
      renderCell: ({ row }) => {
        const buyFund =
          row?.userFunds.length > 0 &&
          row?.userFunds?.find(
            (val) => val?.fundInfo?.name === strings.displayText.fundValue6,
          );
        const sellFund =
          row?.userSalesUnits.length > 0 &&
          row?.userSalesUnits?.find(
            (val) => val?.fundInfo?.name === strings.displayText.fundValue6,
          );
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {buyFund
              ? CurrencyFormater(Number(buyFund?.splitAmount))
              : CurrencyFormater(Number(sellFund?.unitAmount)) || '-'}
          </Typography>
        );
      },
    },
  ];

  const totalTableColumns: GridColumns<GridValidRowModel> = [
    {
      field: 'status',
      headerName: 'status',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.status}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.status}
        </Typography>
      ),
    },
    {
      field: 'totalContribution',
      headerName: 'totalContribution',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.totalContribution}
        </Typography>
      ),
      renderCell: ({ row }) => {
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {CurrencyFormater(Number(row?.totalContribution))}{' '}
          </Typography>
        );
      },
    },
    {
      field: 'totalFund1',
      headerName: 'totalFund1',
      width: 500,
      renderHeader: () => {
        const descObj = getDescriptionAndShortDescription(
          allMasterFunds,
          strings.displayText.fundValue1,
        );

        return (
          <Tooltip title={descObj.description} placement="top">
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {`${strings.displayText.total} ${descObj.shortDescription}`}
            </Typography>
          </Tooltip>
        );
      },
      renderCell: ({ row }) => {
        const fundValue = formatToSevenFixedDecimals(
          row?.funds?.find((val) => val.fundId === 1)?.value ?? 0,
        );
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {CurrencyFormater(Number(fundValue))}
          </Typography>
        );
      },
    },
    {
      field: 'totalFund2',
      headerName: 'totalFund2',
      width: 500,
      renderHeader: () => {
        const descObj = getDescriptionAndShortDescription(
          allMasterFunds,
          strings.displayText.fundValue2,
        );
        return (
          <Tooltip title={descObj.description} placement="top">
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {`${strings.displayText.total} ${descObj?.shortDescription}`}
            </Typography>
          </Tooltip>
        );
      },
      renderCell: ({ row }) => {
        const fundValue = formatToSevenFixedDecimals(
          row?.funds?.find((val) => val.fundId === 2)?.value ?? 0,
        );
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {CurrencyFormater(Number(fundValue))}
          </Typography>
        );
      },
    },
    {
      field: 'totalFund3',
      headerName: 'totalFund3',
      width: 400,
      renderHeader: () => {
        const descObj = getDescriptionAndShortDescription(
          allMasterFunds,
          strings.displayText.fundValue3,
        );
        return (
          <Tooltip title={descObj.description} placement="top">
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {`${strings.displayText.total} ${descObj.shortDescription}`}
            </Typography>
          </Tooltip>
        );
      },
      renderCell: ({ row }) => {
        const fundValue = formatToSevenFixedDecimals(
          row?.funds?.find((val) => val.fundId === 3)?.value ?? 0,
        );
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {CurrencyFormater(Number(fundValue))}
          </Typography>
        );
      },
    },
    {
      field: 'totalFund4',
      headerName: 'totalFund4',
      width: 400,
      renderHeader: () => {
        const descObj = getDescriptionAndShortDescription(
          allMasterFunds,
          strings.displayText.fundValue4,
        );
        return (
          <Tooltip title={descObj.description} placement="top">
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {`${strings.displayText.total} ${descObj.shortDescription}`}
            </Typography>
          </Tooltip>
        );
      },
      renderCell: ({ row }) => {
        const fundValue = formatToSevenFixedDecimals(
          row?.funds?.find((val) => val.fundId === 4)?.value ?? 0,
        );
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {CurrencyFormater(Number(fundValue))}
          </Typography>
        );
      },
    },
    {
      field: 'totalFund5',
      headerName: 'totalFund5',
      width: 400,
      renderHeader: () => {
        const descObj = getDescriptionAndShortDescription(
          allMasterFunds,
          strings.displayText.fundValue5,
        );
        return (
          <Tooltip title={descObj.description} placement="top">
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {`${strings.displayText.total} ${descObj.shortDescription}`}
            </Typography>
          </Tooltip>
        );
      },
      renderCell: ({ row }) => {
        const fundValue = formatToSevenFixedDecimals(
          row?.funds?.find((val) => val.fundId === 5)?.value ?? 0,
        );
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {CurrencyFormater(Number(fundValue))}
          </Typography>
        );
      },
    },
    {
      field: 'totalFund6',
      headerName: 'totalFund6',
      width: 300,
      renderHeader: () => {
        const descObj = getDescriptionAndShortDescription(
          allMasterFunds,
          strings.displayText.fundValue6,
        );
        return (
          <Tooltip title={descObj.description} placement="top">
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {`${strings.displayText.total} ${descObj.shortDescription}`}
            </Typography>
          </Tooltip>
        );
      },
      renderCell: ({ row }) => {
        const fundValue = formatToSevenFixedDecimals(
          row?.funds?.find((val) => val.fundId === 6)?.value ?? 0,
        );
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {CurrencyFormater(Number(fundValue))}
          </Typography>
        );
      },
    },
  ];

  return (
    <>
      <Grid container spacing={2} alignItems="center">
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.startDate}
            type="date"
            value={filters.startDate}
            onChange={(e) => {
              handleFilterChange('startDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              max: filters.endDate ?? undefined,
            }}
          />
        </Grid>
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.endDate}
            type="date"
            value={filters.endDate}
            onChange={(e) => {
              handleFilterChange('endDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
            error={!!dateError}
            helperText={dateError}
            inputProps={{
              min: filters.startDate ?? undefined,
            }}
          />
        </Grid>
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Status</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              label="Status"
              value={filters.status}
              onChange={(e) => {
                handleFilterChange('status', e.target.value);
              }}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Order Type</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              label="Order Type"
              value={filters.orderType}
              onChange={(e) => {
                handleFilterChange('orderType', e.target.value);
              }}
            >
              {transactionTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.employerId}
            type="text"
            value={filters.employerId}
            onChange={(e) => {
              handleFilterChange('employerId', e.target.value);
            }}
          />
        </Grid>
        <Grid item>
          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            loadingPosition="end"
            loading={filterLoader}
            disabled={!!dateError}
            onClick={() => {
              if (dateError) {
                dispatch(
                  setSnackBarFailed({
                    snackBarMessage: dateError,
                  }),
                );
                return;
              }
              setFilterLoader(true);
              dispatch(setCurrentPage({ key: 'ordersSummary', value: 0 }));
              void getAllOrdersSummary();
              void getAllOrdersTotalSummary();
            }}
          >
            {strings.displayText.filter}
          </LoadingButton>
        </Grid>
        <Grid item>
          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            loadingPosition="end"
            loading={exportLoader}
            onClick={() => {
              void downloadExcelFile();
            }}
          >
            {strings.displayText.export}
          </LoadingButton>
        </Grid>
      </Grid>

      <CustomDataGrid
        columns={totalTableColumns}
        rows={ordersTotalSummaryData as GridRowData[]}
        getRowId={(row: GridRowData) => `${String(row.status)}`}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        tableHeight={'27.5vh'}
        hideFooter={true}
      />

      <CustomDataGrid
        columns={columns}
        rows={ordersSummaryData as GridRowData[]}
        getRowId={(row: GridRowData) => `${String(row.transactionId)}`}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        pageCount={pageCount}
        currentPage="ordersSummary"
        sortModel={checkSortingValues(pageInfo)}
        sortingMode={strings.displayText.sortingServer}
        onSortModelChange={(mode: unknown) => {
          dispatch(
            setSortBy({
              key: 'ordersSummary',
              value: mode[0].field,
            }),
          );
          dispatch(
            setSortOrder({
              key: 'ordersSummary',
              value: mode[0].sort,
            }),
          );
          dispatch(
            setCurrentPage({
              key: 'ordersSummary',
              value: 0,
            }),
          );
        }}
      />

      <DialogBox
        open={isModalOpen}
        title="Order Status"
        onClose={handleChatDialogClose}
      >
        <OrderStatusSummary data={orderDialogData} />
      </DialogBox>
    </>
  );
};

export default OrdersSummary;
